import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf,
  EmojiEvents,
  AutoAwesome,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface AwardsCertificatesManagementProps {
  courseId: string;
}

const AwardsCertificatesManagement: React.FC<AwardsCertificatesManagementProps> = ({ courseId }) => {
  const [tabValue, setTabValue] = useState(0);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [awardDialogOpen, setAwardDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Mock data
  const certificates = [
    {
      id: 1,
      name: 'Course Completion Certificate',
      description: 'Awarded upon completing the entire course',
      template: 'default',
      issuedCount: 122,
      issuanceRate: 49.8,
      criteria: { type: 'course_completion', threshold: 100 },
    },
    {
      id: 2,
      name: 'Topic Mastery Certificate',
      description: 'Awarded for mastering specific topics',
      template: 'topic_mastery',
      issuedCount: 89,
      issuanceRate: 36.3,
      criteria: { type: 'topic_mastery', threshold: 90 },
    },
  ];

  const awards = [
    {
      id: 1,
      name: 'Perfect Score Badge',
      type: 'badge',
      description: 'Digital badge for perfect quiz scores',
      earnedCount: 45,
      earnRate: 18.4,
    },
    {
      id: 2,
      name: 'Early Completion Unlock',
      type: 'unlockable',
      description: 'Unlock advanced content for early completion',
      earnedCount: 32,
      earnRate: 13.1,
    },
    {
      id: 3,
      name: 'Study Streak Reward',
      type: 'other',
      description: 'Special reward for maintaining study streak',
      earnedCount: 28,
      earnRate: 11.4,
    },
  ];

  const certificateAnalytics = {
    totalIssued: 211,
    averageTimeToEarn: 38,
    mostEarnedCertificate: 'Course Completion Certificate',
    leastEarnedCertificate: 'Topic Mastery Certificate',
  };

  const certificateIssuanceData = certificates.map(cert => ({
    name: cert.name,
    issued: cert.issuedCount,
  }));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Awards & Certificates Management
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create and manage certificates, awards, and recognition programs for student achievements
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Certificates" />
          <Tab label="Awards" />
          <Tab label="Issue Certificate" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      {/* Certificates Tab */}
      {tabValue === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Certificate Templates</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedItem(null);
                setCertificateDialogOpen(true);
              }}
            >
              Create Certificate
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Certificate Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell align="right">Issued</TableCell>
                  <TableCell align="right">Issuance Rate</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificates.map((certificate) => (
                  <TableRow key={certificate.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PictureAsPdf color="error" />
                        {certificate.name}
                      </Box>
                    </TableCell>
                    <TableCell>{certificate.description}</TableCell>
                    <TableCell>
                      <Chip label={certificate.template} size="small" />
                    </TableCell>
                    <TableCell align="right">{certificate.issuedCount}</TableCell>
                    <TableCell align="right">{certificate.issuanceRate}%</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, certificate)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Awards Tab */}
      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Awards</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedItem(null);
                setAwardDialogOpen(true);
              }}
            >
              Create Award
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Award Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Earned</TableCell>
                  <TableCell align="right">Earn Rate</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {awards.map((award) => (
                  <TableRow key={award.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmojiEvents color="primary" />
                        {award.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={award.type} size="small" />
                    </TableCell>
                    <TableCell>{award.description}</TableCell>
                    <TableCell align="right">{award.earnedCount}</TableCell>
                    <TableCell align="right">{award.earnRate}%</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, award)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Issue Certificate Tab */}
      {tabValue === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Issue Certificate</Typography>
            <Button
              variant="contained"
              startIcon={<AutoAwesome />}
              onClick={() => setIssueDialogOpen(true)}
            >
              Issue Certificate
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Recent Issuances
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Certificate</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>John Doe</TableCell>
                        <TableCell>Course Completion</TableCell>
                        <TableCell>2025-01-15</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Jane Smith</TableCell>
                        <TableCell>Topic Mastery</TableCell>
                        <TableCell>2025-01-14</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Bob Johnson</TableCell>
                        <TableCell>Course Completion</TableCell>
                        <TableCell>2025-01-13</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Automated Issuance Status
                </Typography>
                <Box mt={2}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Auto-issue Course Completion Certificates"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Auto-issue Topic Mastery Certificates"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="Auto-issue Achievement Certificates"
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Analytics Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Certificate Issuance Statistics
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={certificateIssuanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="issued" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Certificate Analytics
              </Typography>
              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Total Issued</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {certificateAnalytics.totalIssued}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Avg Time to Earn</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {certificateAnalytics.averageTimeToEarn} days
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Most Earned</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {certificateAnalytics.mostEarnedCertificate}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Least Earned</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {certificateAnalytics.leastEarnedCertificate}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Certificate Dialog */}
      <Dialog open={certificateDialogOpen} onClose={() => setCertificateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedItem ? 'Edit Certificate' : 'Create New Certificate'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Certificate Name" fullWidth defaultValue={selectedItem?.name || ''} />
            <TextField label="Description" fullWidth multiline rows={2} defaultValue={selectedItem?.description || ''} />
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select label="Template" defaultValue={selectedItem?.template || 'default'}>
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="topic_mastery">Topic Mastery</MenuItem>
                <MenuItem value="course_completion">Course Completion</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Award Criteria Type</InputLabel>
              <Select label="Award Criteria Type" defaultValue={selectedItem?.criteria?.type || 'course_completion'}>
                <MenuItem value="course_completion">Course Completion</MenuItem>
                <MenuItem value="topic_mastery">Topic Mastery</MenuItem>
                <MenuItem value="score_threshold">Score Threshold</MenuItem>
                <MenuItem value="streak">Study Streak</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Threshold" type="number" fullWidth defaultValue={selectedItem?.criteria?.threshold || ''} />
            <FormControlLabel
              control={<Checkbox defaultChecked />}
              label="Automatically issue when criteria is met"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertificateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setCertificateDialogOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Award Dialog */}
      <Dialog open={awardDialogOpen} onClose={() => setAwardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedItem ? 'Edit Award' : 'Create New Award'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Award Name" fullWidth defaultValue={selectedItem?.name || ''} />
            <FormControl fullWidth>
              <InputLabel>Award Type</InputLabel>
              <Select label="Award Type" defaultValue={selectedItem?.type || 'badge'}>
                <MenuItem value="badge">Badge</MenuItem>
                <MenuItem value="certificate">Certificate</MenuItem>
                <MenuItem value="unlockable">Unlockable Content</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Description" fullWidth multiline rows={2} defaultValue={selectedItem?.description || ''} />
            <FormControl fullWidth>
              <InputLabel>Criteria Type</InputLabel>
              <Select label="Criteria Type" defaultValue="quiz_score">
                <MenuItem value="quiz_score">Quiz Score</MenuItem>
                <MenuItem value="completion">Completion</MenuItem>
                <MenuItem value="streak">Streak</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Threshold" type="number" fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAwardDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setAwardDialogOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Issue Certificate Dialog */}
      <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue Certificate</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Certificate</InputLabel>
              <Select label="Certificate" defaultValue="">
                {certificates.map((cert) => (
                  <MenuItem key={cert.id} value={cert.id}>
                    {cert.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Student</InputLabel>
              <Select label="Student" defaultValue="" multiple>
                <MenuItem value="1">John Doe</MenuItem>
                <MenuItem value="2">Jane Smith</MenuItem>
                <MenuItem value="3">Bob Johnson</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Custom Message (optional)" fullWidth multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setIssueDialogOpen(false)}>Issue</Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => {
          if (tabValue === 0) setCertificateDialogOpen(true);
          if (tabValue === 1) setAwardDialogOpen(true);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AwardsCertificatesManagement;

